import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Descriptions,
  Typography,
  Spin,
  Tag,
  message,
  Divider,
  Table,
  Button,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { API_BASE_URL } from "../api/api";

dayjs.extend(utc);
const { Title, Text } = Typography;

const HotelOrderDetailPage = () => {
  const token = localStorage.getItem("token");

  const navigate = useNavigate();
  const { bookingId } = useParams();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const fetchOrderDetail = async () => {
      setLoading(true);
      try {
        if (!token) {
          message.error("User not authenticated.");
          return;
        }
        const res = await fetch(
          `${API_BASE_URL}hotelOrder&booking_id=${bookingId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const json = await res.json();
        if (json.success && json.data) {
          setOrder(json.data);
        } else {
          message.error(json.message || "Failed to load order");
        }
      } catch (error) {
        console.error(error);
        message.error("Error fetching order");
      } finally {
        setLoading(false);
      }
    };

    if (bookingId) {
      fetchOrderDetail();
    }
  }, [bookingId]);

  if (loading) return <Spin size="large" style={{ marginTop: 50 }} />;
  if (!order) return <Text type="danger">Order not found</Text>;

  const {
    hotel,
    holder,
    bookedRooms,
    cancellationPolicies,
    createdAt,
    checkin,
    checkout,
    price,
    currency,
    status,
    hotelConfirmationCode,
  } = order;

  const renderStatusTag = () => {
    let color = "default";
    if (status === "CONFIRMED") color = "green";
    else if (status === "CANCELLED") color = "red";
    return <Tag color={color}>{status}</Tag>;
  };

  const roomColumns = [
    { title: "Guest Name", render: (_, r) => `${r.firstName} ${r.lastName}` },
    { title: "Room Type", dataIndex: ["roomType", "name"] },
    { title: "Adults", dataIndex: "adults" },
    { title: "Children", dataIndex: "children" },
    {
      title: "Price",
      dataIndex: ["rate", "retailRate", "total", "amount"],
      render: (val) => `$${parseFloat(val).toFixed(2)}`,
    },
  ];

  const policyColumns = [
    {
      title: "Cancel Time",
      dataIndex: "cancelTime",
      render: (v) => dayjs.utc(v).local().format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (v) => `$${parseFloat(v).toFixed(2)}`,
    },
    { title: "Type", dataIndex: "type" },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="link"
        onClick={() => navigate(-1)}
        style={{
          marginBottom: 20,
          backgroundColor: "#f0f5ff", // Lighter blue or gray for back button
          color: "#1890ff", // Use blue text to maintain the link style
          border: "1px solid #1890ff", // Subtle border for contrast
          padding: "6px 16px",
          borderRadius: "4px",
          cursor: "pointer",
          fontWeight: "bold", // Makes the text more noticeable
        }}
      >
        ← Back
      </Button>
      <Title level={3}>Hotel Order Detail</Title>

      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Booking ID">
          {order.bookingId}
        </Descriptions.Item>
        <Descriptions.Item label="Hotel Confirmation Code">
          {hotelConfirmationCode || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          {renderStatusTag()}
        </Descriptions.Item>
        <Descriptions.Item label="Created At">
          {dayjs.utc(createdAt).local().format("YYYY-MM-DD HH:mm")}
        </Descriptions.Item>
        <Descriptions.Item label="Check-in">{checkin}</Descriptions.Item>
        <Descriptions.Item label="Check-out">{checkout}</Descriptions.Item>
        <Descriptions.Item label="Total Price">
          ${parseFloat(price).toFixed(2)} {currency}
        </Descriptions.Item>
        <Descriptions.Item label="Hotel Name">{hotel.name}</Descriptions.Item>
      </Descriptions>

      <Divider />

      <Title level={4}>Holder Info</Title>
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Name">
          {holder.firstName} {holder.lastName}
        </Descriptions.Item>
        <Descriptions.Item label="Email">{holder.email}</Descriptions.Item>
      </Descriptions>

      <Divider />

      <Title level={4}>Booked Room(s)</Title>
      <Table
        dataSource={bookedRooms}
        columns={roomColumns}
        rowKey={(_, idx) => idx}
        pagination={false}
        bordered
      />

      {cancellationPolicies?.cancelPolicyInfos?.length > 0 && (
        <>
          <Divider />
          <Title level={4}>Cancellation Policy</Title>
          <Table
            dataSource={cancellationPolicies.cancelPolicyInfos}
            columns={policyColumns}
            rowKey={(rec, i) => i}
            pagination={false}
            bordered
          />
        </>
      )}
    </div>
  );
};

export default HotelOrderDetailPage;
